import { useMyUser, useOrganization } from '@consigli/hooks';
import { type ServiceNameType, Services } from '@consigli/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AGENT_MARK_FALLBACK, AGENT_MARKS } from './agent-marks';

export interface FeedbackServiceOption {
  label: string;
  /** Inline SVG source of the agent's mark. */
  mark: string;
  value: Services;
}

/** UPPER_SNAKE_CASE to the kebab-case the `services.*` keys use. */
const toTranslationKey = (service: Services): string => service.toLowerCase().replace(/_/g, '-');

/** Every `Services` member is also a `ServiceNameType` member of the same value. */
const markFor = (service: Services): string =>
  AGENT_MARKS[service as string as ServiceNameType] ?? AGENT_MARK_FALLBACK;

/**
 * The services a user can file feedback against: their organization's, or every
 * service while the organization has none listed or has not loaded.
 */
export const useFeedbackServiceOptions = (): FeedbackServiceOption[] => {
  const { t } = useTranslation();
  const { user } = useMyUser();
  const { organization } = useOrganization(user.organizationId ?? '');
  const availableServices = organization?.availableServices;

  return useMemo(() => {
    const services =
      availableServices && availableServices.length > 0
        ? availableServices
        : Object.values(Services);

    return services.map((service) => ({
      label: t(`services.${toTranslationKey(service)}`, service),
      mark: markFor(service),
      value: service,
    }));
  }, [t, availableServices]);
};
